/**
 * GenericResourceFilterPanel Component
 * Multi-tenant resource filter panel using tenant configuration
 */

import React, { useMemo, useState, useRef } from 'react';
import {
  Checkbox,
  Radio,
  Button,
  Space,
  Badge,
  Spin,
  Divider,
  Avatar,
  Input,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  DownOutlined,
  RightOutlined,
  SearchOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useResources } from '@/hooks/useCalendars';
import type {
  Resource,
  CalendarEvent,
  ResourceType,
} from '@/api/types/calendars.types';
import type { TenantConfig, ResourceGroupConfig } from '@/config/tenants/types';
import './GenericResourceFilterPanel.css';

// API base URL for avatar images
const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://api.thilo.loc:8000/v2/api';

/**
 * Get avatar URL for a resource if it's linked to a user, business partner, or has metadata avatar
 */
const getResourceAvatarUrl = (
  resource: Resource,
  personTypes: ResourceType[],
  size: '64x64' | '255x255' = '64x64'
): string | null => {
  if (personTypes.includes(resource.resource_type as ResourceType)) {
    // Check for avatar in metadata first (highest priority)
    if (resource.metadata?.avatar_url) {
      return resource.metadata.avatar_url;
    }
    if (resource.metadata?.photo_url) {
      return resource.metadata.photo_url;
    }
    if (resource.metadata?.image_url) {
      return resource.metadata.image_url;
    }

    // Check for linked entity avatar
    if (resource.instance_id) {
      // User avatar
      if (!resource.instance_type || resource.instance_type === 'USER') {
        return `${API_BASE_URL}/tenants/users/id/${resource.instance_id}/avatar.jpg?resize_to=${size}`;
      }
      // Business Partner avatar
      if (resource.instance_type === 'BP') {
        return `${API_BASE_URL}/bp/id/${resource.instance_id}/avatar.jpg?resize_to=${size}`;
      }
    }
  }
  return null;
};

/**
 * Generate tooltip content for person resources
 */
const PersonTooltipContent: React.FC<{
  resource: Resource;
  personTypes: ResourceType[];
}> = ({ resource, personTypes }) => {
  const largeAvatarUrl = getResourceAvatarUrl(resource, personTypes, '255x255');

  return (
    <div className="person-tooltip-content">
      <Avatar
        src={largeAvatarUrl || undefined}
        size={120}
        icon={<UserOutlined />}
        className="person-tooltip-avatar"
        style={{ backgroundColor: resource.metadata?.color || '#1890ff' }}
      />
      <div className="person-tooltip-name">{resource.name}</div>
      {resource.description && (
        <div className="person-tooltip-description">{resource.description}</div>
      )}
    </div>
  );
};

/** Collapsed state for each filter group */
export interface FilterGroupCollapsedState {
  [key: string]: boolean;
}

interface GenericResourceFilterPanelProps {
  /** Tenant configuration */
  config: TenantConfig;
  /** Currently selected resource IDs */
  selectedResourceIds: string[];
  /** Callback when selection changes */
  onSelectionChange: (resourceIds: string[]) => void;
  /** Current calendar view - determines selection mode for some groups */
  currentView?: string;
  /** Pre-fetched resources by type (if provided, panel won't fetch its own) */
  resourcesByType?: Record<string, Resource[]>;
  /** Loading state when resources are provided externally */
  isLoading?: boolean;
  /** Events for cross-filtered event count calculation */
  events?: CalendarEvent[];
  /** Collapsed state for each group */
  collapsedGroups?: FilterGroupCollapsedState;
  /** Callback when collapsed state changes */
  onCollapsedGroupsChange?: (collapsed: FilterGroupCollapsedState) => void;
  /** Date range for event counts */
  dateRange?: { start_date: string; end_date: string };
}

export const GenericResourceFilterPanel: React.FC<
  GenericResourceFilterPanelProps
> = ({
  config,
  selectedResourceIds,
  onSelectionChange,
  currentView = 'week-new',
  resourcesByType: externalResources,
  isLoading: externalLoading,
  events = [],
  collapsedGroups = {},
  onCollapsedGroupsChange,
  dateRange,
}) => {
  // Local state for search terms per group
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  // Local state for showing search input per group
  const [searchVisible, setSearchVisible] = useState<Record<string, boolean>>(
    {}
  );
  // Refs for search inputs to auto-focus
  const searchInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Determine person resource types (for avatar display)
  const personTypes: ResourceType[] = useMemo(() => {
    const types: ResourceType[] = ['INSTRUCTOR', 'PILOT', 'STUDENT', 'PERSON'];
    return types;
  }, []);

  // Toggle collapsed state for a group
  const toggleCollapsed = (groupType: string) => {
    const newCollapsed = {
      ...collapsedGroups,
      [groupType]: !collapsedGroups[groupType],
    };
    onCollapsedGroupsChange?.(newCollapsed);
  };

  // Toggle search visibility
  const toggleSearch = (groupType: string) => {
    const willShow = !searchVisible[groupType];
    setSearchVisible((prev) => ({
      ...prev,
      [groupType]: willShow,
    }));
    if (!willShow) {
      setSearchTerms((prev) => ({
        ...prev,
        [groupType]: '',
      }));
    }
    if (willShow) {
      setTimeout(() => {
        searchInputRefs.current[groupType]?.focus();
      }, 50);
    }
  };

  // Update search term
  const updateSearchTerm = (groupType: string, term: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [groupType]: term,
    }));
  };

  // Filter resources by search term
  const filterResourcesBySearch = (
    resources: Resource[],
    groupType: string
  ): Resource[] => {
    const term = searchTerms[groupType]?.toLowerCase() || '';
    if (!term) return resources;
    return resources.filter((r) => r.name.toLowerCase().includes(term));
  };

  // Check if resources are provided externally
  const hasExternalResources = externalResources !== undefined;

  // Fetch resources for each type from config
  const resourceQueries = config.calendar.resourceTypes.map((type) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data = [], isLoading } = useResources(
      hasExternalResources ? undefined : { resource_type: type, ...dateRange }
    );
    return { type, data, isLoading };
  });

  // Build resources map from queries
  const resourcesMap = useMemo(() => {
    if (hasExternalResources) {
      return externalResources;
    }
    const map: Record<string, Resource[]> = {};
    resourceQueries.forEach(({ type, data }) => {
      map[type] = data;
    });
    return map;
  }, [hasExternalResources, externalResources, resourceQueries]);

  // Check loading state
  const loading = hasExternalResources
    ? (externalLoading ?? false)
    : resourceQueries.some((q) => q.isLoading);

  // Build resource groups for display based on config
  const resourceGroups = useMemo(() => {
    return config.calendar.resourceGroups.map((groupConfig) => {
      // Get resources for the main type
      let resources = resourcesMap[groupConfig.type] || [];

      // Include additional types if specified
      if (groupConfig.includeTypes) {
        groupConfig.includeTypes.forEach((additionalType) => {
          const additional = resourcesMap[additionalType] || [];
          resources = [...resources, ...additional];
        });
      }

      return {
        ...groupConfig,
        resources,
      };
    });
  }, [config.calendar.resourceGroups, resourcesMap]);

  // All resources combined
  const allResources = useMemo(() => {
    return resourceGroups.flatMap((g) => g.resources);
  }, [resourceGroups]);

  // Calculate cross-filtered event counts
  const crossFilteredEventCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    if (!events || events.length === 0) {
      return counts;
    }

    // Group selected IDs by resource type
    const selectedByGroup: Record<string, string[]> = {};
    resourceGroups.forEach((group) => {
      const groupIds = new Set(group.resources.map((r) => r.id));
      selectedByGroup[group.type] = selectedResourceIds.filter((id) =>
        groupIds.has(id)
      );
    });

    // Helper: Check if event matches a set of required resource IDs
    const eventMatchesGroup = (
      event: CalendarEvent,
      requiredIds: string[]
    ): boolean => {
      if (requiredIds.length === 0) return true;
      const eventResourceIds = event.resources.map((r) => r.resource_id);
      return requiredIds.some((id) => eventResourceIds.includes(id));
    };

    // For each resource, calculate matching events
    for (const resource of allResources) {
      let matchingEvents = 0;

      for (const event of events) {
        const eventResourceIds = event.resources.map((r) => r.resource_id);

        if (!eventResourceIds.includes(resource.id)) continue;

        // Check if event matches OTHER selected filters
        let matchesOtherFilters = true;

        const resourceGroupType = resourceGroups.find((g) =>
          g.resources.some((r) => r.id === resource.id)
        )?.type;

        resourceGroups.forEach((group) => {
          if (group.type !== resourceGroupType) {
            if (!eventMatchesGroup(event, selectedByGroup[group.type] || [])) {
              matchesOtherFilters = false;
            }
          }
        });

        if (matchesOtherFilters) {
          matchingEvents++;
        }
      }

      counts[resource.id] = matchingEvents;
    }

    return counts;
  }, [events, allResources, resourceGroups, selectedResourceIds]);

  // Helper to get event count for a resource
  const getEventCount = (resource: Resource): number => {
    if (events && events.length > 0) {
      return crossFilteredEventCounts[resource.id] || 0;
    }
    return resource.event_count || 0;
  };

  // Check if single selection mode for a group
  const isSingleSelectMode = (groupConfig: ResourceGroupConfig): boolean => {
    if (!groupConfig.singleSelectInViews) return false;
    return groupConfig.singleSelectInViews.includes(currentView);
  };

  // Handle individual checkbox change
  const handleCheckboxChange = (
    resourceId: string,
    groupConfig: ResourceGroupConfig
  ) => {
    if (isSingleSelectMode(groupConfig)) {
      // Radio button behavior
      const groupIds =
        resourceGroups
          .find((g) => g.type === groupConfig.type)
          ?.resources.map((r) => r.id) || [];
      const currentSelection = selectedResourceIds.filter((id) =>
        groupIds.includes(id)
      );

      if (currentSelection.includes(resourceId)) {
        return; // Can't deselect in radio mode
      } else {
        const nonGroupIds = selectedResourceIds.filter(
          (id) => !groupIds.includes(id)
        );
        onSelectionChange([...nonGroupIds, resourceId]);
      }
      return;
    }

    // Default multi-select behavior
    const newSelection = selectedResourceIds.includes(resourceId)
      ? selectedResourceIds.filter((id) => id !== resourceId)
      : [...selectedResourceIds, resourceId];
    onSelectionChange(newSelection);
  };

  // Select all in group
  const handleSelectAll = (groupResources: Resource[]) => {
    const groupIds = groupResources.map((r) => r.id);
    const otherIds = selectedResourceIds.filter((id) => !groupIds.includes(id));
    onSelectionChange([...otherIds, ...groupIds]);
  };

  // Clear all in group
  const handleClearAll = (groupResources: Resource[]) => {
    const groupIds = groupResources.map((r) => r.id);
    const newSelection = selectedResourceIds.filter(
      (id) => !groupIds.includes(id)
    );
    onSelectionChange(newSelection);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    onSelectionChange([]);
  };

  // Count selected in group
  const getSelectedCount = (groupResources: Resource[]): number => {
    const groupIds = groupResources.map((r) => r.id);
    return selectedResourceIds.filter((id) => groupIds.includes(id)).length;
  };

  if (loading) {
    return (
      <div className="resource-filter-panel">
        <div className="filter-panel-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="resource-filter-panel">
      <div className="filter-panel-header">
        <h3>Filteri</h3>
        {selectedResourceIds.length > 0 && (
          <Button
            type="link"
            size="small"
            onClick={handleClearAllFilters}
            danger
          >
            Poništi sve ({selectedResourceIds.length})
          </Button>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div className="filter-groups">
        {resourceGroups.map((group) => {
          const selectedCount = getSelectedCount(group.resources);
          const allSelected = selectedCount === group.resources.length;
          const isCollapsed = collapsedGroups[group.type] ?? false;
          const isSearchVisibleForGroup = searchVisible[group.type] ?? false;
          const searchTerm = searchTerms[group.type] ?? '';
          const filteredResources = filterResourcesBySearch(
            group.resources,
            group.type
          );
          const isSingleSelect = isSingleSelectMode(group);

          return (
            <div
              key={group.type}
              className={`filter-group ${isCollapsed ? 'collapsed' : ''}`}
            >
              <div className="group-header">
                <span
                  className="group-title clickable"
                  onClick={() => toggleCollapsed(group.type)}
                >
                  <span className="collapse-icon">
                    {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                  </span>
                  <span className="group-icon">{group.icon}</span>
                  {group.title}
                  {selectedCount > 0 && (
                    <Badge
                      count={selectedCount}
                      style={{
                        backgroundColor: 'var(--color-primary)',
                        marginLeft: 8,
                      }}
                    />
                  )}
                </span>
                <Space size={4}>
                  <Button
                    type="text"
                    size="small"
                    icon={
                      isSearchVisibleForGroup ? (
                        <CloseOutlined />
                      ) : (
                        <SearchOutlined />
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSearch(group.type);
                    }}
                    className="group-action-btn"
                  />
                  {!allSelected &&
                    group.resources.length > 0 &&
                    !isSingleSelect && (
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAll(group.resources);
                        }}
                      >
                        Svi
                      </Button>
                    )}
                  {selectedCount > 0 && !isSingleSelect && (
                    <Button
                      type="link"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearAll(group.resources);
                      }}
                    >
                      Poništi
                    </Button>
                  )}
                </Space>
              </div>

              {isSearchVisibleForGroup && !isCollapsed && (
                <div className="group-search">
                  <Input
                    ref={(el) => {
                      if (el) searchInputRefs.current[group.type] = el.input;
                    }}
                    placeholder="Pretraži..."
                    size="small"
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) =>
                      updateSearchTerm(group.type, e.target.value)
                    }
                    allowClear
                  />
                </div>
              )}

              {isCollapsed && selectedCount > 0 && (
                <div className="collapsed-summary">
                  {group.resources
                    .filter((r) => selectedResourceIds.includes(r.id))
                    .map((r) => r.name)
                    .join(', ')}
                </div>
              )}

              {!isCollapsed && (
                <div className="group-checkboxes">
                  {filteredResources.length === 0 ? (
                    <div className="no-resources">
                      {searchTerm
                        ? 'Nema rezultata pretrage'
                        : 'Nema dostupnih resursa'}
                    </div>
                  ) : isSingleSelect ? (
                    filteredResources.map((resource) => (
                      <Radio
                        key={resource.id}
                        checked={selectedResourceIds.includes(resource.id)}
                        onChange={() =>
                          handleCheckboxChange(resource.id, group)
                        }
                        className="resource-checkbox"
                      >
                        <span className="resource-item-content">
                          <span className="resource-name">{resource.name}</span>
                          {getEventCount(resource) > 0 && (
                            <Badge
                              count={getEventCount(resource)}
                              size="small"
                              style={{
                                backgroundColor: '#52c41a',
                                marginLeft: 6,
                              }}
                            />
                          )}
                          {!resource.is_available && (
                            <span className="resource-unavailable">
                              (nedostupan)
                            </span>
                          )}
                        </span>
                      </Radio>
                    ))
                  ) : (
                    filteredResources.map((resource) => {
                      const avatarUrl = getResourceAvatarUrl(
                        resource,
                        personTypes
                      );
                      const isPerson = personTypes.includes(
                        resource.resource_type as ResourceType
                      );

                      const checkboxContent = (
                        <Checkbox
                          key={resource.id}
                          checked={selectedResourceIds.includes(resource.id)}
                          onChange={() =>
                            handleCheckboxChange(resource.id, group)
                          }
                          className="resource-checkbox"
                        >
                          <span className="resource-item-content">
                            {isPerson && (
                              <Avatar
                                src={avatarUrl || undefined}
                                size={24}
                                icon={<UserOutlined />}
                                className="resource-avatar"
                                style={{
                                  backgroundColor:
                                    resource.metadata?.color || '#1890ff',
                                }}
                              />
                            )}
                            <span className="resource-name">
                              {resource.name}
                            </span>
                            {getEventCount(resource) > 0 && (
                              <Badge
                                count={getEventCount(resource)}
                                size="small"
                                style={{
                                  backgroundColor: '#52c41a',
                                  marginLeft: 6,
                                }}
                              />
                            )}
                            {!resource.is_available && (
                              <span className="resource-unavailable">
                                (nedostupan)
                              </span>
                            )}
                          </span>
                        </Checkbox>
                      );

                      if (isPerson) {
                        return (
                          <Tooltip
                            key={resource.id}
                            title={
                              <PersonTooltipContent
                                resource={resource}
                                personTypes={personTypes}
                              />
                            }
                            placement="rightTop"
                            mouseEnterDelay={1}
                            color="var(--tooltip-bg, rgba(0, 0, 0, 0.85))"
                            overlayClassName="person-tooltip-overlay"
                          >
                            {checkboxContent}
                          </Tooltip>
                        );
                      }

                      return checkboxContent;
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
